import React, { createContext, useContext, useState, useEffect } from 'react';
import { getLocalItem, setLocalItem } from '../../shared/utils/config/localStorage';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Safe read for initial state (e.g. Instagram in-app browser may block localStorage)
function getInitialDarkMode() {
  try {
    const saved = getLocalItem('darkMode', true);
    return typeof saved === 'boolean' ? saved : true;
  } catch {
    return true;
  }
}

export const ThemeProvider = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(getInitialDarkMode);

  // Initialize theme immediately on mount
  useEffect(() => {
    const savedTheme = getInitialDarkMode();
    
    // Apply theme immediately without waiting for state update
    if (savedTheme) {
      document.documentElement.classList.add('dark-mode');
      document.body.classList.add('dark-mode');
      document.documentElement.classList.remove('light-mode');
      document.body.classList.remove('light-mode');
      document.documentElement.setAttribute('data-theme', 'dark');
    } else {
      document.documentElement.classList.add('light-mode');
      document.body.classList.add('light-mode');
      document.documentElement.classList.remove('dark-mode');
      document.body.classList.remove('dark-mode');
      document.documentElement.setAttribute('data-theme', 'light');
    }
  }, []); // Run only once on mount

  useEffect(() => {
    setLocalItem('darkMode', isDarkMode);
    
    // Apply theme to document body and html immediately
    if (isDarkMode) {
      document.documentElement.classList.add('dark-mode');
      document.body.classList.add('dark-mode');
      document.documentElement.classList.remove('light-mode');
      document.body.classList.remove('light-mode');
      document.documentElement.setAttribute('data-theme', 'dark');
    } else {
      document.documentElement.classList.add('light-mode');
      document.body.classList.add('light-mode');
      document.documentElement.classList.remove('dark-mode');
      document.body.classList.remove('dark-mode');
      document.documentElement.setAttribute('data-theme', 'light');
    }
    
    // Force a re-render by dispatching a custom event
    window.dispatchEvent(new CustomEvent('themeChanged', { 
      detail: { isDarkMode } 
    }));
  }, [isDarkMode]);

  const toggleDarkMode = () => {
    setIsDarkMode(prev => !prev);
  };

  const value = {
    isDarkMode,
    toggleDarkMode,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}; 